import { Drawer, styled } from '@mui/material';
import { useSidebarLayout } from 'contexts/sidebarLayout';
import { forwardRef, useEffect, useRef } from 'react';
import Box from 'shell/components/Box';

const DEVICES = ['mobile', 'tablet', 'desktop'];
const PERSISTENT_NAVIGATION_OFFSET_TRANSITION = 'left 250ms ease';

const CustomDrawer = styled(Drawer, {
    shouldForwardProp: (prop) => !['$device', '$sidebarOffset'].includes(prop),
})(({ $device: device, $sidebarOffset: sidebarOffset, anchor, theme }) => {
    return {
        ...(device === 'mobile' && { zIndex: theme.zIndex.drawer + 4 }),
        overflowY: 'hidden',
        '& .MuiBackdrop-root': {
            borderRadius: 0,
            background: 'transparent',
        },
        ...(anchor === 'left' &&
            sidebarOffset > 0 && {
                left: sidebarOffset,
                transition: PERSISTENT_NAVIGATION_OFFSET_TRANSITION,
                '& .MuiDrawer-paper': {
                    position: 'absolute',
                    left: 0,
                },
            }),
        '& > .MuiPaper-root': {
            borderRadius: 0,
            boxShadow: theme.palette.ensightful.shadows[2],
        },
    };
});

const DrawerInner = styled(Box)(({ theme, device }) => {
    const width = {
        mobile: '100vw',
        tablet: '365px',
        desktop: '444px',
    }[device];

    return {
        width,
        padding: theme.spacing(5),
        overflowY: 'auto',
    };
});

export default forwardRef(({ children, device = 'tablet', offsetForSidebar = false, variant, ...rest }, ref) => {
    if (!DEVICES.includes(device)) throw new Error(`Shell: Invalid device "${device}" in component Drawer.`);

    const { sidebarWidth } = useSidebarLayout();
    const drawerInnerRef = useRef(null);

    useEffect(() => {
        if (rest.open && drawerInnerRef.current) drawerInnerRef.current.scrollTo(0, 0);
    }, [rest.open]);

    return (
        <CustomDrawer
            ref={ref}
            $device={device}
            $sidebarOffset={offsetForSidebar ? sidebarWidth : 0}
            transitionDuration={300}
            role={variant !== 'persistent' && rest.open ? 'main' : 'region'}
            variant={variant}
            {...rest}
        >
            <DrawerInner ref={drawerInnerRef} device={device}>
                {children}
            </DrawerInner>
        </CustomDrawer>
    );
});
